import { Search, X } from 'lucide-react';
import { ANY } from '../../core/fleet-filters';
import { TASK_DATE_RANGES, TASK_DATE_RANGE_LABELS } from '../../core/task-filters';
import type { TaskFilters } from '../../core/task-filters';
import { TASK_STATUSES, TASK_STATUS_LABELS } from '../../types/task';
import type { TaskType } from '../../types/task';

interface TaskFilterBarProps {
  filters: TaskFilters;
  taskTypes: readonly TaskType[];
  onChange: (next: TaskFilters) => void;
  /** How many tasks the current filters keep, for the live hint line. */
  resultCount: number;
}

const selectClass =
  'h-10 rounded-xl border border-line bg-panel px-3 text-sm text-ink/80 focus:outline-none focus:border-brand-500';

/**
 * Search + filters for the tasks board: free text, the day being planned, the
 * type of work and the lifecycle status. Priority has its own tab strip above
 * the grid, because it is the filter a manager reaches for first.
 */
export function TaskFilterBar({ filters, taskTypes, onChange, resultCount }: TaskFilterBarProps) {
  const set = <K extends keyof TaskFilters>(key: K, value: TaskFilters[K]) =>
    onChange({ ...filters, [key]: value });

  return (
    <div className="bg-panel border border-line rounded-2xl p-3 sm:p-4 space-y-3">
      <div className="relative">
        <Search
          size={18}
          className="absolute top-1/2 -translate-y-1/2 right-3 text-ink/40"
          aria-hidden
        />
        <input
          type="search"
          value={filters.text}
          onChange={(event) => set('text', event.target.value)}
          placeholder="חיפוש לפי כותרת, יעד, איש קשר או טלפון"
          aria-label="חיפוש משימה"
          className="w-full h-11 rounded-xl border border-line bg-canvas pr-10 pl-10 text-sm focus:outline-none focus:border-brand-500 focus:bg-panel"
        />
        {filters.text ? (
          <button
            type="button"
            onClick={() => set('text', '')}
            title="ניקוי חיפוש"
            className="absolute top-1/2 -translate-y-1/2 left-2 w-7 h-7 rounded-lg flex items-center justify-center text-ink/40 hover:text-ink"
          >
            <X size={16} aria-hidden />
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={filters.dateRange}
          onChange={(event) => set('dateRange', event.target.value as TaskFilters['dateRange'])}
          aria-label="סינון לפי תאריך"
          className={selectClass}
        >
          {TASK_DATE_RANGES.map((range) => (
            <option key={range} value={range}>
              {TASK_DATE_RANGE_LABELS[range]}
            </option>
          ))}
        </select>

        <select
          value={filters.taskTypeId}
          onChange={(event) => set('taskTypeId', event.target.value)}
          aria-label="סינון לפי סוג משימה"
          className={selectClass}
        >
          <option value={ANY}>כל סוגי המשימות</option>
          {taskTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>

        <select
          value={filters.statusId}
          onChange={(event) => set('statusId', event.target.value as TaskFilters['statusId'])}
          aria-label="סינון לפי סטטוס"
          className={selectClass}
        >
          <option value={ANY}>כל הסטטוסים</option>
          {TASK_STATUSES.map((status) => (
            <option key={status} value={status}>
              {TASK_STATUS_LABELS[status]}
            </option>
          ))}
        </select>
      </div>

      <p className="text-xs text-ink/50">{resultCount} משימות מוצגות</p>
    </div>
  );
}
